import { isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
// eslint-disable-next-line import-whitelist -- needed for both core and content
import { getOutputByName } from '../../core/h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { isOrthographicVideoOutput } from '../../cross-team-shared/misc-utils/output-utils';
import { getProjectionType } from '../../cross-team-shared/misc-utils/projection-utils';
import {
  assertDefined,
  getOrThrow,
} from '../../cross-team-shared/misc-utils/strict-record';
import { CloudShaderMaterial } from '../materials/cloud/cloud-shader-material';
import { setViewBoxHighlightUniforms } from '../materials/cloud/shaders/color/modes/viewbox-highlight';
import {
  ProjectionCamera,
  updateProjectionCamera,
} from '../renderers/projection/projection-camera';
import { PointCloudContentState } from '../state/point-cloud-content-state';
import { getCameraData } from './calibration-utils';
import {
  assignProjectionDefines,
  setProjectionUniforms,
} from './materials/material-utils';

export function isRenderingWithViewBoxHighlight(state: PointCloudContentState) {
  const { viewBoxHighlightEnabled, project, splitViewOutputName } = state;

  if (isUndefined(splitViewOutputName)) {
    return false;
  }

  const isSplitViewVideoLoaded = isDefined(
    state.projectionImageSize[splitViewOutputName],
  );

  return (
    isSplitViewVideoLoaded &&
    viewBoxHighlightEnabled &&
    !isOrthographicVideoOutput(getOutputByName(project, splitViewOutputName))
  );
}

export function updateSplitViewOutputForHighlight(
  material: CloudShaderMaterial,
  state: PointCloudContentState,
  splitViewCamera: ProjectionCamera,
) {
  const { render, projectionImageSize, calibrationData } = state;
  const { frameNumber, calibrationOptions } = render;
  const splitViewOutputName = getSplitViewOutputNameOrThrow(state);
  const splitViewCalibrationData = calibrationData[splitViewOutputName];
  const projectionType = getProjectionType(
    splitViewCalibrationData,
    state.task,
    splitViewOutputName,
  );
  const imageSize = getOrThrow(projectionImageSize, splitViewOutputName);

  const cameraData = getCameraData(
    splitViewCalibrationData,
    calibrationOptions[splitViewOutputName],
    frameNumber,
  );

  assignProjectionDefines(material, projectionType);
  updateProjectionCamera(splitViewCamera, cameraData, imageSize);
}

export function updateMaterialForHighlight(
  material: CloudShaderMaterial,
  state: PointCloudContentState,
  splitViewCamera: ProjectionCamera,
) {
  const splitViewOutputName = getSplitViewOutputNameOrThrow(state);
  const { calibrationData } = state;
  const splitViewCalibrationData = calibrationData[splitViewOutputName];
  const projectionType = getProjectionType(
    splitViewCalibrationData,
    state.task,
    splitViewOutputName,
  );

  setProjectionUniforms(material, {
    currentOutputName: splitViewOutputName,
    projectionType,
    state,
  });
  setViewBoxHighlightUniforms(material, state, splitViewCamera);
}

export function getSplitViewOutputNameOrThrow(state: PointCloudContentState) {
  assertDefined(state.splitViewOutputName);
  return state.splitViewOutputName;
}
